import isEmpty from 'lodash/isEmpty';
import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { geocodeByAddress, getLatLng } from 'react-places-autocomplete';
import { fetchInstoreLocations } from 'core/actions/ocapi';
import { resetCardLinkData } from 'core/modules/Instore/CardLinkOffer/actions';
import { selectUserIsIdentified, selectUser } from 'core/selectors/user';
import { selectInstoreLinkCardResponse } from 'core/modules/Instore/CardLinkOffer/selector';
import { selectSiteUrl, selectGoogleMapApiKey } from 'core/selectors/app';
import ComponentErrorBoundary from 'core/modules/ErrorBoundaries/ComponentErrorBoundary';
import SectionTitle from 'core/modules/SectionTitle/SectionTitle';
import { selectIsMobileMQ } from 'core/modules/MediaQueries/selectors';
import { useScript } from 'core/hooks/useScript';
import ContentWrap from 'core/modules/Partials/ContentWrap/ContentWrap';

import Offer from '../OffersSection/components/Offer/Offer';
import { selectInstoreOffersConfig, selectInstoreOffersIsLoading } from '../OffersSection/selectors';

import { instoreOfferSetLocation, instoreUserLocation } from '../OffersSection/actions';
import { selectInstoreLocationsConfig } from '../Map/selectors';

import { useGetHeading, useGetContent } from './hooks';

import './InstoreCuratedOffers.scss';

function InstoreCuratedOffers() {
  const isMobileMQ = useSelector(selectIsMobileMQ);
  const locationsConfig = useSelector(selectInstoreLocationsConfig);
  const config = useSelector(selectInstoreOffersConfig);
  const isLoading = useSelector(selectInstoreOffersIsLoading);
  const instoreCardResponse = useSelector(selectInstoreLinkCardResponse);
  const isUserIdentified = useSelector(selectUserIsIdentified);
  const googleMapApiKey = useSelector(selectGoogleMapApiKey);
  const user = useSelector(selectUser);
  const siteUrl = useSelector(selectSiteUrl);
  const dispatch = useDispatch();
  const { checkShouldShowViewAllLink, shouldUseGoogleMap } = config;
  const shouldShowViewAllLink = checkShouldShowViewAllLink(isMobileMQ);
  const { headingData } = useGetHeading();
  const { sectionTitle } = headingData;
  const { instoreOffers } = useGetContent();

  const { zipcode } = user;
  const getLocations = (location) => {
    if (!isLoading) {
      const { api: { options } } = locationsConfig;
      dispatch(fetchInstoreLocations(location, options));
    }
  };

  const initMapCallback = () => {
    window.navigator.geolocation.getCurrentPosition((pos) => {
      const { coords } = pos;
      const { latitude, longitude } = coords;
      const location = {
        lat: latitude,
        lng: longitude,
      };
      dispatch(instoreOfferSetLocation(location));
      dispatch(instoreUserLocation(location));
      getLocations(location);
    }, err => {
      if (err.message === 'User denied Geolocation') {
        geocodeByAddress(zipcode)
          .then((results) => getLatLng(results[0]))
          .then(({ lat, lng }) => {
            const currentLocation = {
              lat,
              lng,
            };

            dispatch(instoreOfferSetLocation(currentLocation));
            dispatch(instoreUserLocation(currentLocation));
            getLocations(currentLocation);
          })
          .catch(() => {
            console.log(`couldnt fetch lng and lat for zipcode ${zipcode}`);
          });
      }
    });
  };

  // For better performance script in loaded only if there are instore offers to show.
  // After script is loaded initMapCallback is triggered
  window.initMapCallback = initMapCallback;
  const googleMapScriptUrl =
    `https://maps.googleapis.com/maps/api/js?key=${googleMapApiKey}&libraries=places,geometry&callback=initMapCallback`;
  useScript({ url: googleMapScriptUrl, shouldAppendScript: shouldUseGoogleMap && !isEmpty(instoreOffers) });

  /**
   * Need to reset card link data when instore offers are fetched
   * Incase of add card and delete card to maintain clean state.
   */
  useEffect(() => {
    if (instoreCardResponse.length) {
      dispatch(resetCardLinkData());
    }
  }, [instoreOffers, instoreCardResponse]);

  const renderFlexRowFillers = () => (
    <>
      <span className="mn_flexRowFiller" /><span className="mn_flexRowFiller" />
      <span className="mn_flexRowFiller" /><span className="mn_flexRowFiller" />
    </>
  );

  if (isEmpty(instoreOffers)) {
    return null;
  }

  return (
    <div className="mn_instoreCuratedOffersSection">
      <ContentWrap mobile tablet l xl>
        <SectionTitle
          title={sectionTitle}
          viewAllLinkUrl={isMobileMQ ? '' : `${siteUrl}/instore`}
          viewAllLinkTitle="View all in store offers"
        />
        <div className="mn_instoreOffers">
          {instoreOffers.map(offer => (
            <Offer offer={offer} key={offer.offerId} isUserIdentified={isUserIdentified} />
          ))}
          { !isMobileMQ && renderFlexRowFillers() }
          { shouldShowViewAllLink &&
          <div className="mn_viewAllLinkMobileWrap">
            <a className="mn_viewAllLink mn_button mn_secondaryButton" href={`${siteUrl}/instore`}>View all offers</a>
          </div> }
        </div>
      </ContentWrap>
    </div>
  );
}

export default (props) => <ComponentErrorBoundary><InstoreCuratedOffers {...props} /></ComponentErrorBoundary>;
